import React, { useState } from 'react';
import { Play, Star, Video, FileText, Clock, CheckCircle2, XCircle, User } from 'lucide-react';
import StarRating from './StarRating';

const STATUS_STYLES = {
    pending: { bg: 'bg-amber-50', text: 'text-amber-700', icon: Clock, label: 'Pending' },
    approved: { bg: 'bg-green-50', text: 'text-green-700', icon: CheckCircle2, label: 'Approved' },
    rejected: { bg: 'bg-red-50', text: 'text-red-700', icon: XCircle, label: 'Rejected' },
};

const TYPE_ICONS = { video: Video, text: FileText, mixed: FileText };

export default function TestimonialCard({ submission, onClick }) {
    const status = STATUS_STYLES[submission.status] || STATUS_STYLES.pending;
    const StatusIcon = status.icon;
    const TypeIcon = TYPE_ICONS[submission.type] || FileText;

    // Extract first text answer for preview
    const textPreview = submission.answers
        ? Object.values(submission.answers).find(v => typeof v === 'string' && v.length > 10)
        : null;

    return (
        <div onClick={onClick}
            className="bg-white border border-stone-200 rounded-xl overflow-hidden hover:shadow-md transition-shadow cursor-pointer group">
            {/* Video thumbnail or text preview */}
            {submission.type === 'video' || submission.video_s3_key ? (
                <div className="aspect-video bg-stone-900 relative flex items-center justify-center">
                    {submission.thumbnail_url ? (
                        <img src={submission.thumbnail_url} alt="" className="w-full h-full object-cover" />
                    ) : (
                        <Video className="w-10 h-10 text-white/30" />
                    )}
                    <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity bg-black/20">
                        <div className="w-12 h-12 rounded-full bg-white/90 flex items-center justify-center">
                            <Play className="w-5 h-5 text-stone-900 ml-0.5" />
                        </div>
                    </div>
                </div>
            ) : textPreview ? (
                <div className="p-4 bg-stone-50 border-b border-stone-100">
                    <p className="text-sm text-stone-600 line-clamp-3 italic">"{textPreview}"</p>
                </div>
            ) : null}

            <div className="p-3 space-y-2">
                {/* Rating */}
                {submission.overall_rating > 0 && (
                    <StarRating value={submission.overall_rating} maxStars={5} size="sm" readonly />
                )}

                {/* Contact info */}
                <div className="flex items-center gap-2">
                    <div className="w-7 h-7 rounded-full bg-stone-100 flex items-center justify-center flex-shrink-0">
                        <User className="w-3.5 h-3.5 text-stone-500" />
                    </div>
                    <div className="min-w-0">
                        <div className="text-sm font-medium text-stone-800 truncate">{submission.contact_name || 'Unknown'}</div>
                        {submission.sent_by_name && (
                            <div className="text-xs text-stone-400 truncate">Sent by {submission.sent_by_name}</div>
                        )}
                    </div>
                </div>

                {/* Footer */}
                <div className="flex items-center justify-between pt-1">
                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${status.bg} ${status.text}`}>
                        <StatusIcon className="w-3 h-3" /> {status.label}
                    </span>
                    <div className="flex items-center gap-1 text-xs text-stone-400">
                        <TypeIcon className="w-3 h-3" />
                        {submission.submitted_at
                            ? new Date(submission.submitted_at).toLocaleDateString()
                            : 'Not submitted'}
                    </div>
                </div>
            </div>
        </div>
    );
}
